import MapboxDraw, { DrawMode } from "@mapbox/mapbox-gl-draw";
import { booleanEqual, feature } from "@turf/turf";
import { Feature } from "geojson";
import { useEffect, useState } from "react";

import ExtendedDirectSelectMode from "./modeDirectSelect";
import ExtendedLineStringMode from "./modeLinestring";
import ExtendedPolygonMode from "./modePolygon";
import styles from "./styles";

type UseMapDrawParams = {
    map: mapboxgl.Map | null;
    enabled?: boolean;
    selectedFeatureId?: string;
    selectedFeature?: Feature;
    onDeselect: () => void;
    onFeatureCreate(feature: Feature): void;
    onFeatureUpdate(feature: Feature): void;
    selectedDrawMode: DrawMode;
};

export function useMapDraw({
    map,
    enabled = true,
    selectedFeatureId,
    selectedFeature,
    onDeselect,
    onFeatureCreate,
    onFeatureUpdate,
    selectedDrawMode,
}: UseMapDrawParams): MapboxDraw | undefined {
    const draw = useMapboxDrawInstance(map, enabled);
    useMapDrawSelectionSync(draw, selectedFeatureId, selectedFeature);
    useMapDrawGeometrySync(draw, selectedFeature);
    useMapDrawUpdateDrawMode(draw, selectedDrawMode);
    useMapDrawEvents(map, enabled, selectedFeature, onDeselect, onFeatureCreate, onFeatureUpdate);

    return draw;
}

function useMapboxDrawInstance(map: mapboxgl.Map | null, enabled: boolean) {
    const [draw, setDraw] = useState<MapboxDraw | undefined>();

    useEffect(() => {
        if (!map || !enabled) return;

        const drawControl = new MapboxDraw({
            displayControlsDefault: false,
            modes: {
                ...MapboxDraw.modes,
                draw_line_string: ExtendedLineStringMode,
                draw_polygon: ExtendedPolygonMode,
                direct_select: ExtendedDirectSelectMode,
            },
            styles: [...styles],
        });
        map.addControl(drawControl);
        setDraw(drawControl);

        return () => {
            try {
                map.removeControl(drawControl);
            } catch {
                // Sometimes mapDraw loses the map before it's removed and causes a crash.
            }
            setDraw(undefined);
        };
    }, [map, enabled]);

    return draw;
}

function useMapDrawSelectionSync(
    draw: MapboxDraw | undefined,
    selectedFeatureId: string | undefined,
    selectedFeature: Feature | undefined,
) {
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally leaving out selectedFeature, we split it off into its own geometry sync. including it causes strange side effects with freezing map pans
    useEffect(() => {
        if (!draw) return;

        // if no selected feature anymore, we remove any reference we may have of it still
        if (!selectedFeatureId) {
            if (draw.getAll().features.length > 0) draw.deleteAll();
            return;
        }

        // if the selected feature does not have data reference of ours, we forget it.
        const id = selectedFeature?.properties?.id;
        if (!selectedFeature || !id) return;

        // remove any references to previously selected features
        const others = draw
            .getAll()
            .features.filter(f => f.id !== id)
            .map(f => String(f.id ?? ""));
        if (others.length > 0) draw.delete(others);

        // if we do not have reference to the selected feature, add the feature to the draw object
        if (draw.get(id) === undefined) {
            draw.add(feature(selectedFeature.geometry, selectedFeature.properties, { id }));
        }

        // mapbox draw does not support direct select for points
        const desiredMode = selectedFeature.geometry.type === "Point" ? "simple_select" : "direct_select";
        // only change mode if we have not selected it yet
        const isSelected = draw.getMode() === desiredMode && draw.getSelectedIds().includes(id);
        if (!isSelected) {
            if (desiredMode === "simple_select") {
                draw.changeMode("simple_select", { featureIds: [id] });
            } else {
                draw.changeMode("direct_select", { featureId: id });
            }
        }
    }, [draw, selectedFeatureId, selectedFeature?.properties?.id]);
}

// update geometry in mapdraw state after it gets updated by eg a refetch
function useMapDrawGeometrySync(draw: MapboxDraw | undefined, selectedFeature: Feature | undefined) {
    useEffect(() => {
        const id = selectedFeature?.properties?.id;
        if (!draw || !selectedFeature || !id) return;

        const existing = draw.get(id);
        if (existing && booleanEqual(existing, selectedFeature)) return;

        draw.add(feature(selectedFeature.geometry, selectedFeature.properties, { id }));
    }, [draw, selectedFeature]);
}

function useMapDrawUpdateDrawMode(draw: MapboxDraw | undefined, selectedDrawMode: DrawMode) {
    useEffect(() => {
        if (!draw || draw.getMode() === selectedDrawMode) return;
        // @ts-expect-error the mapbox typing is very specific for "direct_select", but we dont use that mode here
        draw.changeMode(selectedDrawMode);
    }, [draw, selectedDrawMode]);
}

function useMapDrawEvents(
    map: mapboxgl.Map | null,
    enabled: boolean,
    selectedFeature: Feature | undefined,
    onDeselect: () => void,
    onFeatureCreate: (feature: Feature) => void,
    onFeatureUpdate: (feature: Feature) => void,
) {
    useEffect(() => {
        if (!map || !enabled) return;

        function handleOnDrawCreate(e: MapboxDraw.DrawCreateEvent) {
            onFeatureCreate(e.features[0]);
        }

        function handleOnDrawUpdate(e: MapboxDraw.DrawUpdateEvent) {
            onFeatureUpdate(e.features[0]);
        }

        function handleModeChange(e: MapboxDraw.DrawModeChangeEvent) {
            if (e.mode !== "direct_select" && selectedFeature?.geometry.type !== "Point") {
                onDeselect();
            }
        }

        map.on("draw.create", handleOnDrawCreate).on("draw.update", handleOnDrawUpdate).on("draw.modechange", handleModeChange);

        return () => {
            map.off("draw.create", handleOnDrawCreate)
                .off("draw.update", handleOnDrawUpdate)
                .off("draw.modechange", handleModeChange);
        };
    }, [map, enabled, selectedFeature, onDeselect, onFeatureCreate, onFeatureUpdate]);
}
